import React, { useState, useRef, useEffect } from 'react';
import { Typography, Box, IconButton, Stack } from '@mui/material';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Pause from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import { ISound } from '../../../types/global/helper';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';

export interface AudioCardProps {
    soundData: ISound;
    isEditMode?: boolean;
    onDelete?: (sound: ISound) => void;
}

const WAVEFORM_BARS = 36;

const AudioCard: React.FC<AudioCardProps> = ({ soundData, isEditMode = false, onDelete }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [waveform, setWaveform] = useState<number[]>([]);
    const [, setIsScrubbing] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const waveformRef = useRef<HTMLDivElement | null>(null);
    const { themeStyle } = useThemeContext();

    const primary = theme[themeStyle].highlight;
    const playButtonHover = theme[themeStyle].primary_orange || theme[themeStyle].highlight;
    /** Card shell — “surface container low” */
    const cardSurface = '#1a1a1a';
    /** Header strip — slightly lighter than body */
    const headerSurface = 'rgba(255,255,255,0.06)';
    /** Playback row under header */
    const bodySurface = 'rgba(0,0,0,0.25)';
    /** Inset waveform track */
    const trackBg = '#000000';
    const titleColor = theme[themeStyle].true_white || theme[themeStyle].tag_white;
    const subTextColor = 'rgba(255,255,255,0.55)';

    // Fetch and decode audio for waveform
    useEffect(() => {
        let isMounted = true;
        setWaveform([]);
        if (!soundData.sound) return;
        fetch(soundData.sound)
            .then(res => res.arrayBuffer())
            .then(arrayBuffer => {
                const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                return audioContext.decodeAudioData(arrayBuffer);
            })
            .then(audioBuffer => {
                if (!isMounted) return;
                const rawData = audioBuffer.getChannelData(0); // Use first channel
                const samples = WAVEFORM_BARS;
                const blockSize = Math.floor(rawData.length / samples);
                const waveformData: number[] = [];
                for (let i = 0; i < samples; i++) {
                    let sum = 0;
                    for (let j = 0; j < blockSize; j++) {
                        sum += Math.abs(rawData[i * blockSize + j]);
                    }
                    waveformData.push(sum / blockSize);
                }
                // Normalize
                const max = Math.max(...waveformData);
                setWaveform(waveformData.map(v => v / (max || 1)));
            })
            .catch(() => {
                // fallback: flat waveform
                setWaveform(Array(WAVEFORM_BARS).fill(0.5));
            });
        return () => {
            isMounted = false;
        };
    }, [soundData.sound]);

    const handlePlayPause = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
    };

    // Scrubbing
    const handleWaveformPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!audioRef.current || !waveformRef.current || !duration) return;
        setIsScrubbing(true);
        handleWaveformPointerMove(e);
        window.addEventListener('pointermove', handleWaveformPointerMove);
        window.addEventListener('pointerup', handleWaveformPointerUp);
    };

    const handleWaveformPointerMove = (e: PointerEvent | React.PointerEvent) => {
        if (!audioRef.current || !waveformRef.current || !duration) return;
        const rect = waveformRef.current.getBoundingClientRect();
        const x = (e as PointerEvent).clientX - rect.left;
        const percent = Math.max(0, Math.min(1, x / rect.width));
        audioRef.current.currentTime = percent * duration;
        setCurrentTime(audioRef.current.currentTime);
    };

    const handleWaveformPointerUp = () => {
        setIsScrubbing(false);
        window.removeEventListener('pointermove', handleWaveformPointerMove);
        window.removeEventListener('pointerup', handleWaveformPointerUp);
    };

    const formatTime = (time: number) => {
        if (!Number.isFinite(time) || time < 0) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // Highlighted bars for progress
    const playedBars = duration && waveform.length ? Math.floor((currentTime / duration) * waveform.length) : 0;

    const subtitle =
        [soundData.artist, soundData.tag].filter(Boolean).join(' · ') ||
        soundData.sound_category?.replace(/_/g, ' ') ||
        '';

    const bars =
        waveform.length > 0
            ? waveform
            : Array.from({ length: WAVEFORM_BARS }, (_, i) => 0.35 + (i % 7) * 0.08);

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100%',
                borderRadius: '18px',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.05)',
                bgcolor: cardSurface,
                position: 'relative',
            }}>
            {isEditMode && (
                <IconButton
                    onClick={() => onDelete && onDelete(soundData)}
                    aria-label="Remove audio"
                    sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                        color: 'white',
                        width: 26,
                        height: 26,
                        zIndex: 2,
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.85)' },
                    }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            )}

            {/* Header: title + squircle play */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.25, sm: 1.5 },
                    bgcolor: headerSurface,
                    gap: 1.5,
                }}>
                <Box sx={{ minWidth: 0, pr: isEditMode ? 3 : 0 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 700,
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            color: titleColor,
                            lineHeight: 1.2,
                        }}>
                        {soundData.title?.trim() || 'Audio reel'}
                    </Typography>
                    {subtitle ? (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '0.75rem',
                                color: subTextColor,
                                mt: 0.25,
                                lineHeight: 1.3,
                            }}>
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
                <IconButton
                    onClick={handlePlayPause}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                    sx={{
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        borderRadius: '10px',
                        bgcolor: primary,
                        color: theme[themeStyle].black,
                        boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
                        transition: 'transform 0.15s ease, background-color 0.15s ease',
                        '&:hover': { bgcolor: playButtonHover, color: theme[themeStyle].black },
                        '&:active': { transform: 'scale(0.92)' },
                    }}>
                    {isPlaying ? (
                        <Pause sx={{ fontSize: 20 }} />
                    ) : (
                        <PlayArrow sx={{ fontSize: 22, ml: 0.125 }} />
                    )}
                </IconButton>
            </Stack>

            {/* Playback strip: black track + bars + live time */}
            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.25, sm: 1.5 }, bgcolor: bodySurface }}>
                <Box
                    ref={waveformRef}
                    onPointerDown={handleWaveformPointerDown}
                    sx={{
                        height: 36,
                        width: '100%',
                        bgcolor: trackBg,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.5,
                        gap: 1,
                        cursor: duration ? 'pointer' : 'default',
                        userSelect: 'none',
                        overflow: 'hidden',
                    }}>
                    <Stack
                        direction="row"
                        alignItems="flex-end"
                        justifyContent="flex-start"
                        spacing={0.375}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            height: 18,
                            overflow: 'hidden',
                        }}>
                        {bars.map((h, i) => {
                            const t = Math.max(0.08, Math.min(1, h));
                            const barH = 4 + t * 14;
                            const isPlayed = i < playedBars;
                            const shade = 0.25 + ((i * 3) % 5) * 0.12;
                            return (
                                <Box
                                    key={i}
                                    sx={{
                                        width: 3,
                                        height: `${barH}px`,
                                        flexShrink: 0,
                                        borderRadius: '999px',
                                        bgcolor: primary,
                                        opacity: isPlayed ? Math.min(1, 0.55 + shade) : shade,
                                    }}
                                />
                            );
                        })}
                    </Stack>
                    <Typography
                        component="span"
                        sx={{
                            ml: 'auto',
                            flexShrink: 0,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            fontSize: '0.6875rem',
                            color: subTextColor,
                            whiteSpace: 'nowrap',
                        }}>
                        {formatTime(currentTime)} / {formatTime(duration || 0)}
                    </Typography>
                </Box>
            </Box>

            <audio
                ref={audioRef}
                src={soundData.sound}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={handleEnded}
                style={{ display: 'none' }}
            />
        </Box>
    );
};

export { AudioCard };
